import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useParams,
} from 'react-router-dom'
import { lazy, Suspense, type ReactNode } from 'react'

import HomePage from '@/pages/HomePage/HomePage'
import UIPage from '@/pages/UIPage/UIPage'
import { useReferralCapture } from '@/hooks/useReferralCapture'
import { useAssistantPlatformEnabled } from '@/hooks/useAssistantPlatformEnabled'
import { useTwinChatEnabled } from '@/hooks/useTwinChatEnabled'
import { TwinChatUnavailable } from '@/pages/ChatPage/TwinChatUnavailable'
import { ChatPageErrorBoundary } from '@/pages/ChatPage/ChatPageErrorBoundary'
import { useAuth } from '@/lib/config/auth-provider'
import { ROUTES } from '@/routes/routes'
// Home Lab prototype. Lazy so the parallel routes cost the live app nothing.
const HomeLabPage = lazy(() => import('@/pages/HomeLabPage/HomeLabPage'))
const AssistantLabPage = import.meta.env.DEV
  ? lazy(() => import('@/pages/AssistantLabPage/AssistantLabPage'))
  : null
// Assistant Platform v1 Step 6.1 (docs/plans/assistant-platform-v1-execution-plan.md):
// read-only workflows lobby + detail. Lazy so this parallel surface costs the live app nothing.
const WorkflowsPage = lazy(() => import('@/pages/WorkflowsPage/WorkflowsPage'))
const WorkflowDetailPage = lazy(() => import('@/pages/WorkflowsPage/WorkflowDetailPage'))
import LoginPage from '@/pages/LoginPage/LoginPage'
import RegisterPage from '@/pages/RegisterPage/RegisterPage'
import RegisterVerifyPage from '@/pages/RegisterVerifyPage/RegisterVerifyPage'
import RegisterCompletePage from '@/pages/RegisterCompletePage/RegisterCompletePage'
import RegisterSsoCompletePage from '@/pages/RegisterSsoCompletePage/RegisterSsoCompletePage'
import ForgotPasswordPage from '@/pages/ForgotPasswordPage/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/ResetPasswordPage/ResetPasswordPage'
import TestsPage from '@/features/studies/list/page/TestsPage'
import AudiencesPage from '@/pages/AudiencesPage/AudiencesPage'
import AudiencesNewPage from '@/pages/AudiencesNewPage/AudiencesNewPage'
import AudienceDetailPage from '@/pages/AudienceDetailPage/AudienceDetailPage'
import AudienceEditPage from '@/pages/AudienceEditPage/AudienceEditPage'
import AudienceEditStagePage from '@/pages/AudienceEditStagePage/AudienceEditStagePage'
import AudienceEditFiltersPage from '@/pages/AudienceEditFiltersPage/AudienceEditFiltersPage'
import AudienceImportsSessionLayout from '@/layouts/AudienceImportSessionLayout/SessionRoute/layout'
import SettingsPage from '@/pages/SettingsPage/SettingsPage'
import SettingsBillingPlansPage from '@/pages/SettingsBillingPlansPage/SettingsBillingPlansPage'
import KnowledgeHubPage from '@/pages/KnowledgeHubPage/KnowledgeHubPage'
import KnowledgeUploadPage from '@/pages/KnowledgeUploadPage/KnowledgeUploadPage'
import KnowledgeDocumentPage from '@/pages/KnowledgeDocumentPage/KnowledgeDocumentPage'
import ThreadsPage from '@/pages/ThreadsPage/ThreadsPage'
import ThreadDetailPage from '@/pages/ThreadDetailPage/ThreadDetailPage'
import SharedRoomPage from '@/pages/SharedRoomPage/SharedRoomPage'
import ConversationsPage from '@/pages/ConversationsPage/ConversationsPage'
import CreateStudyRoute from '@/features/studies/routes/CreateStudyRoute'
import StudyReportRoute from '@/features/studies/routes/StudyReportRoute'
import InternalDeckRenderRoute from '@/features/studies/deck/render/InternalDeckRenderRoute'
import LPStudyPreviewPage from '@/pages/LPStudyPreviewPage/LPStudyPreviewPage'
import ShareTokenRoute from '@/features/studies/sharing/routes/ShareTokenRoute'

const AudienceInviteAcceptPage = lazy(() => import('@/pages/AudienceInviteAcceptPage/AudienceInviteAcceptPage'))
const SlackConnectPage = lazy(() => import('@/pages/SlackConnectPage/SlackConnectPage'))
const ChatPage = lazy(() => import('@/pages/ChatPage/ChatPage'))
const ArtifactPreviewBedPage = import.meta.env.DEV
  ? lazy(() => import('@/pages/ArtifactPreviewBedPage/ArtifactPreviewBedPage'))
  : null
const StudyDocPrintPreviewPage = import.meta.env.DEV
  ? lazy(() => import('@/pages/StudyDocPrintPreviewPage/StudyDocPrintPreviewPage'))
  : null

import {
  AdminToBackofficeRedirect,
  KitchenSinkToBackofficeRedirect,
} from '@/spa/BackofficeRedirects'
import { SpaAuthLayout } from '@/spa/layouts/SpaAuthLayout'
import { SpaAudiencesLayout } from '@/spa/layouts/SpaAudiencesLayout'
import { SpaResultsLayout } from '@/spa/layouts/SpaResultsLayout'
import { SpaAppShellLayout } from '@/spa/layouts/SpaAppShellLayout'
import { SharedAppShellLayout } from '@/spa/layouts/SharedAppShellLayout'

function AudienceImportsRedirect() {
  return <Navigate to="/audiences/new" replace />
}

function TestDetailRedirect() {
  const { id } = useParams()
  if (!id) {
    return <Navigate to="/tests" replace />
  }
  return <Navigate to={`/tests/${id}/results`} replace />
}

function RoomsRedirect() {
  const { id } = useParams()
  return <Navigate to={`/chats/${id}`} replace />
}

/**
 * Frontend review BLOCKER 1 (item 5): route-level gate for the two Workflows routes,
 * matching this file's own existing pattern for an unreachable route (`<Navigate
 * to={ROUTES.home} replace />`, the catch-all `*` route below) rather than inventing a
 * new fallback. `useWorkflowTemplates` resolves its fixture client-side regardless of
 * the backend gate, so a flag-off user who types `/workflows` directly must be turned
 * back here: hiding the sidebar link (`Sidebar.tsx`) only stops the click, not the URL.
 */
function WorkflowsRoute() {
  const assistantPlatformEnabled = useAssistantPlatformEnabled()
  if (!assistantPlatformEnabled) {
    return <Navigate to={ROUTES.home} replace />
  }
  return (
    <Suspense fallback={null}>
      <WorkflowsPage />
    </Suspense>
  )
}

function WorkflowDetailRoute() {
  const assistantPlatformEnabled = useAssistantPlatformEnabled()
  if (!assistantPlatformEnabled) {
    return <Navigate to={ROUTES.home} replace />
  }
  return (
    <Suspense fallback={null}>
      <WorkflowDetailPage />
    </Suspense>
  )
}

/**
 * Assistant beta launch Step 7 (docs/plans/assistant-beta-launch-plan.md):
 * close the bookmark path for `/threads/:token`. Flag-off redirects to the Threads
 * lobby (`ROUTES.threads`), not home — the lobby itself owns the locked empty state
 * (Step 4). Unlike `WorkflowsRoute`, hold while `useAuth().isLoading` so a cold load
 * does not bounce a flag-on user off their own deep link before the profile resolves
 * (`useAssistantPlatformEnabled` returns false for an unresolved profile).
 */
function ThreadDetailRoute() {
  const { isLoading } = useAuth()
  const assistantPlatformEnabled = useAssistantPlatformEnabled()

  if (isLoading) {
    return null
  }
  if (!assistantPlatformEnabled) {
    return <Navigate to={ROUTES.threads} replace />
  }
  return <ThreadDetailPage />
}

/**
 * Same Step 7 gate for `/knowledge/upload`. Flag-off redirects to the Library lobby.
 * Hold while the profile is loading (see ThreadDetailRoute).
 */
function KnowledgeUploadRoute() {
  const { isLoading } = useAuth()
  const assistantPlatformEnabled = useAssistantPlatformEnabled()

  if (isLoading) {
    return null
  }
  if (!assistantPlatformEnabled) {
    return <Navigate to={ROUTES.knowledge} replace />
  }
  return <KnowledgeUploadPage />
}

/**
 * Same Step 7 gate for `/knowledge/:documentToken`. Flag-off redirects to the Library
 * lobby. Hold while the profile is loading (see ThreadDetailRoute).
 */
function KnowledgeDocumentRoute() {
  const { isLoading } = useAuth()
  const assistantPlatformEnabled = useAssistantPlatformEnabled()

  if (isLoading) {
    return null
  }
  if (!assistantPlatformEnabled) {
    return <Navigate to={ROUTES.knowledge} replace />
  }
  return <KnowledgeDocumentPage />
}

/**
 * Step 12 (docs/plans/assistant-mvp-plan.md, D1d) — the referral-capture home for
 * whichever page mounts at `/`, `ROUTES.homeLab` or `ROUTES.homeClassic`. Wraps ONLY
 * those three routes (nested one level inside `SpaAppShellLayout` below), not every
 * route `SpaAppShellLayout` renders — the redirect-to-register condition is unchanged
 * from `HomePage`'s original effect, and scoping it to these three routes keeps it from
 * firing on an authenticated app route that happens to carry a stray `?ref=` param.
 */
function HomeEntryLayout() {
  useReferralCapture()
  return <Outlet />
}

function TwinChatGate(props: { children: ReactNode }) {
  const twinChatEnabled = useTwinChatEnabled()
  if (!twinChatEnabled) {
    return <TwinChatUnavailable />
  }
  return props.children
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/admin" element={<AdminToBackofficeRedirect />} />
      <Route path="/admin/*" element={<AdminToBackofficeRedirect />} />
      <Route
        path="/internal/kitchen-sink"
        element={<KitchenSinkToBackofficeRedirect />}
      />
      <Route
        path="/internal/kitchen-sink/*"
        element={<KitchenSinkToBackofficeRedirect />}
      />

      <Route element={<SharedAppShellLayout />}>
        <Route path="/share/:token" element={<ShareTokenRoute />} />
        <Route path="/share/room/:token" element={<SharedRoomPage />} />
        <Route path="/audiences/invites/:token/accept" element={<Suspense fallback={<div />}><AudienceInviteAcceptPage /></Suspense>} />
        <Route path={ROUTES.slackConnect} element={<Suspense fallback={<div />}><SlackConnectPage /></Suspense>} />
      </Route>

      <Route
        path="/internal/deck-render"
        element={<InternalDeckRenderRoute />}
      />

      <Route
        path="/internal/lp-preview"
        element={<LPStudyPreviewPage />}
      />
      {ArtifactPreviewBedPage && (
        <Route
          path={ROUTES.ARTIFACT_PREVIEW_BED}
          element={<Suspense fallback={null}><ArtifactPreviewBedPage /></Suspense>}
        />
      )}
      {StudyDocPrintPreviewPage && (
        <Route
          path={ROUTES.STUDY_DOC_PRINT_PREVIEW}
          element={<Suspense fallback={null}><StudyDocPrintPreviewPage /></Suspense>}
        />
      )}

      {/* Assistant Lab renders OUTSIDE SpaAppShellLayout on purpose: it brings its own
          stripped sidebar, and the app shell's rail would sit next to it. DEV-only: a
          prototype stub UI with no network calls, never registered in a production build. */}
      {AssistantLabPage && (
        <Route
          path={ROUTES.assistantLab}
          element={<Suspense fallback={<div className="min-h-screen bg-background" />}><AssistantLabPage /></Suspense>}
        />
      )}

      <Route element={<SpaAuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/register/verify" element={<RegisterVerifyPage />} />
        <Route
          path="/register/complete"
          element={<RegisterCompletePage />}
        />
        <Route
          path="/register/sso/complete"
          element={<RegisterSsoCompletePage />}
        />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Route>

      <Route element={<SpaAppShellLayout />}>
        <Route element={<HomeEntryLayout />}>
          {/* Step 11 (docs/plans/assistant-mvp-plan.md): `/` now mounts HomeLabPage.
              `homeLab` is kept as an extra path to the same page so existing
              links/muscle memory keep working — both render HomeLabPage. */}
          <Route
            path={ROUTES.home}
            element={<Suspense fallback={<div className="min-h-screen bg-background" />}><HomeLabPage /></Suspense>}
          />
          <Route
            path={ROUTES.homeLab}
            element={<Suspense fallback={<div className="min-h-screen bg-background" />}><HomeLabPage /></Suspense>}
          />
          {/* Pre-Step-11 homepage, kept reachable as a fallback while Home Lab is finished. */}
          <Route path={ROUTES.homeClassic} element={<HomePage />} />
        </Route>
        <Route path="/ui" element={<UIPage />} />

        <Route path="/tests" element={<TestsPage />} />
        <Route path="/tests/new" element={<CreateStudyRoute />} />
        <Route path="/tests/:id/results" element={<SpaResultsLayout />}>
          <Route index element={<StudyReportRoute />} />
        </Route>
        <Route path="/tests/:id" element={<TestDetailRedirect />} />

        <Route path="/audiences" element={<SpaAudiencesLayout />}>
          <Route index element={<AudiencesPage />} />
          <Route path="new" element={<AudiencesNewPage />} />
          <Route path=":id" element={<AudienceDetailPage />} />
          <Route path=":id/edit" element={<AudienceEditPage />} />
          <Route
            path=":id/edit-stage"
            element={<AudienceEditStagePage />}
          />
          <Route path=":id/edit-filters" element={<AudienceEditFiltersPage />} />
          <Route path="imports" element={<AudienceImportsRedirect />} />
          <Route
            path="imports/:sessionId/*"
            element={<AudienceImportsSessionLayout />}
          />
        </Route>

        <Route path="/chats" element={<TwinChatGate><ConversationsPage /></TwinChatGate>} />
        <Route path="/chats/new" element={<TwinChatGate><Navigate to="/chats?new=1" replace /></TwinChatGate>} />
        <Route
          path="/chats/:id"
          element={
            <TwinChatGate>
              <ChatPageErrorBoundary>
                <Suspense fallback={null}>
                  <ChatPage />
                </Suspense>
              </ChatPageErrorBoundary>
            </TwinChatGate>
          }
        />

        {/* Legacy chat notification links */}
        <Route path="/chat/:id" element={<RoomsRedirect />} />

        {/* Legacy /rooms redirects */}
        <Route path="/rooms" element={<Navigate to="/chats" replace />} />
        <Route path="/rooms/new" element={<Navigate to="/chats?new=1" replace />} />
        <Route path="/rooms/:id" element={<RoomsRedirect />} />


        <Route path={ROUTES.knowledge} element={<KnowledgeHubPage />} />
        <Route path={ROUTES.knowledgeUpload} element={<KnowledgeUploadRoute />} />
        <Route path="/knowledge/:documentToken" element={<KnowledgeDocumentRoute />} />
        <Route path={ROUTES.threads} element={<ThreadsPage />} />
        <Route path={`${ROUTES.threads}/:token`} element={<ThreadDetailRoute />} />

        <Route path={ROUTES.workflows} element={<WorkflowsRoute />} />
        <Route path={`${ROUTES.workflows}/:slug`} element={<WorkflowDetailRoute />} />

        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/billing/plans" element={<SettingsBillingPlansPage />} />
        <Route
          path="/settings/knowledge/new"
          element={<Navigate to="/knowledge/upload" replace />}
        />
        <Route
          path="/settings/knowledge/:id"
          element={<Navigate to="/knowledge" replace />}
        />

        <Route path="*" element={<Navigate to={ROUTES.home} replace />} />
      </Route>
    </Routes>
  )
}
